import { decodeErrorResult } from 'viem';
import type { Hex } from 'viem';

import { positionRouterAbi } from 'libs/contracts';

export interface DecodedPositionRouterRevert {
  errorName: string;
  message: string;
}

const formatBigIntArg = (value: unknown): string => {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

export const decodePositionRouterRevert = (
  data: Hex | undefined,
): DecodedPositionRouterRevert | undefined => {
  if (!data || data === '0x') {
    return undefined;
  }

  try {
    const decoded = decodeErrorResult({
      abi: positionRouterAbi,
      data,
    });

    const errorName = decoded.errorName;
    const args = (decoded.args ?? []) as readonly unknown[];

    switch (errorName) {
      case 'PositionRouter_ZeroAddress':
        return { errorName, message: 'A required address was unset.' };
      case 'PositionRouter_ZeroAmount':
        return { errorName, message: 'Amount must be greater than zero.' };
      case 'PositionRouter_MarketNotListed':
        return {
          errorName,
          message: `Market is not listed on the Comptroller (${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_DeadlineExpired':
        return {
          errorName,
          message: 'The transaction deadline expired. Refresh and try again.',
        };
      case 'PositionRouter_DirectTaoForbidden':
        return {
          errorName,
          message:
            'The router does not accept direct native TAO sends. Use the named entry points.',
        };
      case 'PositionRouter_NotDelegate':
        return {
          errorName,
          message:
            'The router has not been approved as a delegate. Complete the one-time setup and retry.',
        };
      case 'PositionRouter_MintBehalfFailed':
        return {
          errorName,
          message: `Supply failed in the market (Compound error code ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_RedeemBehalfFailed':
        return {
          errorName,
          message: `Withdrawal failed in the market (Compound error code ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_BorrowBehalfFailed':
        return {
          errorName,
          message: `Borrow failed in the market (Compound error code ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_RepayBorrowBehalfFailed':
        return {
          errorName,
          message: `Repay failed in the market (Compound error code ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_EnterMarketBehalfFailed':
        return {
          errorName,
          message: `Joining the market failed (Compound error code ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_NativeNotExpected':
        return {
          errorName,
          message: 'This entry point does not accept native TAO. Drop the value field and retry.',
        };
      case 'PositionRouter_NativeRefundFailed':
        return {
          errorName,
          message: 'Refund of native TAO failed.',
        };
      // Contract arg order for the four multi-arg errors below:
      //   UnderlyingMismatch(expected, actual)
      //   StakingAllowanceExpired(user, netuid, required)
      //   {Redeem,Borrow}Slippage(minOut, actualOut)
      case 'PositionRouter_UnderlyingMismatch':
        return {
          errorName,
          message: `Underlying wrapper does not match the expected token for this entry point (got ${formatBigIntArg(args[1])}, expected ${formatBigIntArg(args[0])}).`,
        };
      case 'PositionRouter_StakingAllowanceExpired':
        return {
          errorName,
          message: `Per-subnet staking allowance is exhausted (user ${formatBigIntArg(args[0])}, netuid ${formatBigIntArg(args[1])}, required ${formatBigIntArg(args[2])}). Approve the router again on STAKING_V2 for this subnet.`,
        };
      case 'PositionRouter_RedeemSlippage':
        return {
          errorName,
          message: `Withdrawal slippage exceeded: would receive ${formatBigIntArg(args[1])}, minimum was ${formatBigIntArg(args[0])}. Increase tolerance or try a smaller amount.`,
        };
      case 'PositionRouter_BorrowSlippage':
        return {
          errorName,
          message: `Borrow slippage exceeded: would receive ${formatBigIntArg(args[1])}, minimum was ${formatBigIntArg(args[0])}. Increase tolerance or try a smaller amount.`,
        };
      default:
        return { errorName, message: errorName };
    }
  } catch {
    return undefined;
  }
};
